import type { ActiveStudySite } from 'common/auth/types';

import type { DeviceHandoffRejectionReason, DeviceHandoffState, RespondentTarget } from './types';

export interface DeviceHandoffRootState {
    deviceHandoff: DeviceHandoffState;
}

const INACTIVE_STATE: DeviceHandoffState = { phase: 'inactive' };

export function selectDeviceHandoffState(state: DeviceHandoffRootState): DeviceHandoffState {
    return state.deviceHandoff ?? INACTIVE_STATE;
}

export function selectIsDeviceHandoffInProgress(state: DeviceHandoffRootState): boolean {
    return selectDeviceHandoffState(state).phase !== 'inactive';
}

export function selectRespondentTarget(state: DeviceHandoffRootState): RespondentTarget | null {
    const deviceHandoffState = selectDeviceHandoffState(state);

    switch (deviceHandoffState.phase) {
        case 'switch_to_respondent':
        case 'respondent_active':
        case 'switch_to_siteuser':
            return deviceHandoffState.target;
        default:
            return null;
    }
}

export function selectHandoffActiveStudySite(state: DeviceHandoffRootState): ActiveStudySite | null {
    const deviceHandoffState = selectDeviceHandoffState(state);

    switch (deviceHandoffState.phase) {
        case 'switch_to_respondent':
        case 'respondent_active':
        case 'switch_to_siteuser':
            return deviceHandoffState.activeStudySite;
        default:
            return null;
    }
}

export function selectDeviceHandoffRejectionReason(state: DeviceHandoffRootState): DeviceHandoffRejectionReason | null {
    const deviceHandoffState = selectDeviceHandoffState(state);

    if (
        (deviceHandoffState.phase === 'switch_to_respondent' || deviceHandoffState.phase === 'switch_to_siteuser') &&
        deviceHandoffState.step.type === 'rejected'
    ) {
        return deviceHandoffState.step.reason;
    }

    return null;
}

export function selectIsSwitchingToRespondent(state: DeviceHandoffRootState): boolean {
    return selectDeviceHandoffState(state).phase === 'switch_to_respondent';
}

export function selectIsSwitchingToSiteUser(state: DeviceHandoffRootState): boolean {
    return selectDeviceHandoffState(state).phase === 'switch_to_siteuser';
}

export function selectIsRespondentOnHandedOffDevice(state: DeviceHandoffRootState): boolean {
    return selectDeviceHandoffState(state).phase === 'respondent_active';
}

export function selectRespondentActivityLocation(state: DeviceHandoffRootState): string | null {
    const deviceHandoffState = selectDeviceHandoffState(state);

    return deviceHandoffState.phase === 'respondent_active' ? deviceHandoffState.activityLocation ?? null : null;
}

export function selectIsRespondentAcceptingPolicy(state: DeviceHandoffRootState): boolean {
    const deviceHandoffState = selectDeviceHandoffState(state);

    return deviceHandoffState.phase === 'switch_to_respondent' && deviceHandoffState.step.type === 'accepting_policy';
}

export function selectIsDeviceHandoffModalOpen(state: DeviceHandoffRootState): boolean {
    const { phase } = selectDeviceHandoffState(state);

    return phase === 'switch_to_respondent' || phase === 'switch_to_siteuser';
}
